import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { DRAFT_TYPES, confBadge, riskBadge } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Check, X, ShieldWarning, CaretDown, CaretUp, PaperPlaneTilt } from "@phosphor-icons/react";

function ActionCard({ a, user }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState(a.output?.client_facing_text || "");
  const [feedback, setFeedback] = useState("");

  const review = useMutation({
    mutationFn: (verb) => api.post(`/ai/actions/${a.id}/${verb}`, { edited_text: text, feedback }),
    onSuccess: (_, verb) => { toast.success(verb === "approve" ? "Approved & applied" : "Rejected — feedback saved to learning store"); qc.invalidateQueries(); },
    onError: (e) => toast.error(errText(e)),
  });

  const sendEmail = useMutation({
    mutationFn: () => api.post("/integrations/email/send-action", { action_id: a.id }),
    onSuccess: ({ data }) => { toast.success(`Emailed to ${data.to} via ${data.provider}`); qc.invalidateQueries({ queryKey: ["ai-actions"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const gatedForUser = a.requires_cpa_approval && user?.role !== "cpa";
  const o = a.output || {};

  return (
    <div data-testid={`approval-card-${a.id}`} className="rounded-lg border border-zinc-200 bg-white">
      <button onClick={() => setExpanded(!expanded)} data-testid={`approval-expand-${a.id}`} className="w-full flex items-center gap-3 p-4 text-left">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-zinc-900 flex items-center gap-2">
            {DRAFT_TYPES.find((d) => d.key === a.draft_type)?.label || a.draft_type}
            {a.requires_cpa_approval && <span className="flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-1.5 py-0.5"><ShieldWarning size={11} /> CPA gate</span>}
          </p>
          <p className="text-xs text-zinc-500 mt-0.5">{a.client_name} · {new Date(a.created_at).toLocaleString()} · by {a.created_by}</p>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-mono ${confBadge(o.confidence)}`}>conf {Math.round((o.confidence || 0) * 100)}%</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-mono ${riskBadge(o.risk_score)}`}>risk {Math.round((o.risk_score || 0) * 100)}%</span>
        {a.status !== "pending" && <span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase ${a.status === "approved" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}>{a.status}</span>}
        {expanded ? <CaretUp size={14} className="text-zinc-400" /> : <CaretDown size={14} className="text-zinc-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-zinc-100 pt-3">
          {o.internal_cpa_alert && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex gap-2"><ShieldWarning size={14} className="shrink-0 mt-0.5" /> {o.internal_cpa_alert}</div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Internal reasoning</p>
            <p className="text-xs text-zinc-600">{o.internal_reasoning}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Client-facing draft (editable — edits feed the learning store)</p>
            <Textarea data-testid={`approval-draft-textarea-${a.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={8} disabled={a.status !== "pending"} className="font-mono text-xs" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(o.missing_documents || []).map((m) => <span key={m} className="text-[10px] bg-rose-50 text-rose-700 border border-rose-200 rounded-sm px-1.5 py-0.5">missing: {m}</span>)}
            {(o.tasks_created || []).map((t, i) => <span key={`${a.id}-t-${i}-${t.title}`} className="text-[10px] bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5">task: {t.title}</span>)}
            {(o.deadlines || []).map((d, i) => <span key={`${a.id}-d-${i}-${d.label}`} className="text-[10px] bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5 font-mono">{d.label}: {d.date}</span>)}
            {(o.billing_actions || []).map((b, i) => <span key={`${a.id}-b-${i}-${typeof b === "string" ? b.slice(0, 20) : i}`} className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-1.5 py-0.5">{b}</span>)}
            {o.crm_ui_update?.move_to_stage && <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded-sm px-1.5 py-0.5">on approve → {o.crm_ui_update.move_to_stage}</span>}
          </div>
          {a.status === "approved" && (
            <div className="flex items-center gap-2">
              <Button data-testid={`send-email-btn-${a.id}`} size="sm" variant="outline" disabled={sendEmail.isPending} onClick={() => sendEmail.mutate()}>
                <PaperPlaneTilt size={14} className="mr-1" /> {a.emailed_at ? "Resend via email" : "Send via email"}
              </Button>
              {a.emailed_at && <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5">Emailed via {a.emailed_via}</span>}
            </div>
          )}
          {a.status === "pending" && (
            <>
              <Textarea data-testid={`approval-feedback-input-${a.id}`} placeholder="Feedback (required for reject, optional for approve — captured for self-learning)" value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={2} className="text-xs" />
              <div className="flex gap-2">
                <Button data-testid={`approval-approve-btn-${a.id}`} size="sm" disabled={gatedForUser || review.isPending} onClick={() => review.mutate("approve")}>
                  <Check size={14} className="mr-1" /> Approve & apply
                </Button>
                <Button data-testid={`approval-reject-btn-${a.id}`} size="sm" variant="outline" disabled={review.isPending} onClick={() => review.mutate("reject")}>
                  <X size={14} className="mr-1" /> Reject
                </Button>
                {gatedForUser && <p className="text-xs text-amber-600 self-center">Only a CPA can approve this action</p>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ApprovalQueue() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const { data: actions } = useQuery({ queryKey: ["ai-actions", tab], queryFn: () => api.get(`/ai/actions?status=${tab}`).then((r) => r.data) });

  return (
    <div className="p-8 space-y-6 fade-up" data-testid="approvals-page">
      <div>
        <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900">Approval Queue</h1>
        <p className="text-sm text-zinc-500 mt-1">Every AI action requires human review · filings, tax positions & IRS responses gated to CPA</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="approvals-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="approvals-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="approvals-tab-rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="space-y-3">
        {(actions || []).map((a) => <ActionCard key={a.id} a={a} user={user} />)}
        {actions?.length === 0 && <p className="text-sm text-zinc-400 italic">Nothing here.</p>}
      </div>
    </div>
  );
}
